import type { ToolResultErrorCode, ToolResultStatus } from '@proj-airi/core-agent'

import type { ChatHistoryItem, ChatSlicesToolCallResult } from '../types/chat'

import { annotateToolCallResult } from '@proj-airi/core-agent'

import { createToolCallResultLookup } from '../components/scenarios/chat/components/tool-call-results'
import { type ChatConnectionPhase, sanitizeConnectionError } from './chat-connection'

/** Current phase of one agent turn or tool call. */
export type AgentTaskPhase = 'started' | 'running' | 'pending-confirmation' | 'succeeded' | 'failed' | 'timed-out' | 'rejected' | 'cancelled'

/**
 * One structured execution record.
 * The summary is a short redacted line. It is not the chat transcript or the raw tool payload.
 */
export interface AgentTaskRecord {
  id: string
  sessionId: string
  kind: 'turn' | 'tool'
  phase: AgentTaskPhase
  toolName?: string
  startedAt?: number
  endedAt?: number
  durationMs?: number
  errorCode?: ToolResultErrorCode
  summary: string
}

/** Read-only facts shared by the active session, the workbench, and the acceptance control plane. */
export interface VelisRuntimeSnapshot {
  schemaVersion: 1
  generatedAt: string
  session: {
    id: string
    characterId: string
    title: string
    active: true
  }
  character: {
    cardId: string
    name: string
  }
  chat: {
    providerId: string
    modelId: string
    phase: ChatConnectionPhase
  }
  voice: {
    speech: {
      providerId: string
      modelId: string
      voiceId: string
      ready: boolean
    }
    hearing: {
      providerId: string
      modelId: string
      ready: boolean
    }
  }
  tasks: AgentTaskRecord[]
  memory?: { action: 'WRITE' | 'SUPERSEDE' | 'FORGET' | 'SKIP', memoryId?: string, reason: string }
}

const summaryLimit = 280

/**
 * Projects assistant turns and tool calls into task records.
 * User message text stays in the chat store. A missing tool result is still running.
 * An interrupted assistant message with no result for that call is cancelled.
 * A stored `status` wins over result text. Text classification remains for older results.
 *
 * @example
 * projectAgentTasks('s1', [{ role: 'assistant', content: '', id: 'a1', slices: [{ type: 'tool-call', toolCall: { toolCallId: 'c1', toolCallType: 'function', toolName: 'save_local_note', args: '{}' } }], tool_results: [{ id: 'c1', isError: true, result: 'The operation timed out.', status: 'timed-out', errorCode: 'timeout' }] }])
 * // => [{ id: 'a1-tool-0', sessionId: 's1', kind: 'tool', phase: 'timed-out', toolName: 'save_local_note', errorCode: 'timeout', summary: 'The operation timed out.' }, { id: 'a1-turn', sessionId: 's1', kind: 'turn', phase: 'timed-out', errorCode: 'timeout', summary: 'A tool call timed out.' }]
 */
export function projectAgentTasks(sessionId: string, messages: ChatHistoryItem[], secrets: string[] = []): AgentTaskRecord[] {
  const tasks: AgentTaskRecord[] = []

  for (const message of messages) {
    if (message.role === 'error') {
      tasks.push(present({
        id: message.id ?? `error-${tasks.length}`,
        sessionId,
        kind: 'turn',
        phase: 'failed',
        errorCode: 'failed',
        startedAt: message.createdAt,
        summary: clip(redact(textFrom(message.content), secrets)),
      }))
      continue
    }

    if (message.role !== 'assistant' || !('slices' in message))
      continue

    const lookup = createToolCallResultLookup(message.slices, message.tool_results)
    const toolPhases: AgentTaskPhase[] = []
    let openTools = 0
    let toolIndex = 0
    for (const [index, slice] of message.slices.entries()) {
      if (slice.type !== 'tool-call')
        continue
      const outcome = toolPhase(lookup.get(index), message.interrupted === true, secrets)
      if (outcome.phase === 'running' || outcome.phase === 'started' || outcome.phase === 'pending-confirmation')
        openTools += 1
      toolPhases.push(outcome.phase)
      tasks.push(present({
        id: `${message.id ?? 'assistant'}-tool-${toolIndex}`,
        sessionId,
        kind: 'tool',
        phase: outcome.phase,
        toolName: slice.toolCall.toolName,
        startedAt: message.createdAt,
        errorCode: outcome.errorCode,
        summary: outcome.summary,
      }))
      toolIndex += 1
    }

    const reply = message.slices
      .filter(slice => slice.type === 'text')
      .map(slice => slice.text)
      .join('')
      .trim()
    tasks.push(present(turnRecord(message, sessionId, reply, openTools, toolPhases, secrets)))
  }

  return tasks
}

/**
 * Builds the serializable runtime snapshot from the current stores.
 * The snapshot omits keys, system prompts, chat transcripts, tool arguments, and audio.
 *
 * @example
 * buildRuntimeSnapshot({ generatedAt: '2026-10-04T00:00:00.000Z', sessionId: 's1', characterId: 'default', title: '', cardId: 'default', characterName: 'Velis', providerId: 'deepseek', modelId: 'deepseek-flash', phase: 'ready', speech: { providerId: 'speech-noop', modelId: '', voiceId: '', ready: false }, hearing: { providerId: '', modelId: '', ready: false }, messages: [] })
 * // => { schemaVersion: 1, session: { id: 's1', characterId: 'default', title: '', active: true }, tasks: [] }
 */
export function buildRuntimeSnapshot(input: {
  generatedAt: string
  sessionId: string
  characterId: string
  title: string
  cardId: string
  characterName: string
  providerId: string
  modelId: string
  phase: ChatConnectionPhase
  speech: VelisRuntimeSnapshot['voice']['speech']
  hearing: VelisRuntimeSnapshot['voice']['hearing']
  messages: ChatHistoryItem[]
  secrets?: string[]
  memory?: VelisRuntimeSnapshot['memory']
}): VelisRuntimeSnapshot {
  const secrets = input.secrets ?? []
  return {
    schemaVersion: 1,
    generatedAt: input.generatedAt,
    session: {
      id: clip(redact(input.sessionId, secrets), 160),
      characterId: clip(redact(input.characterId, secrets), 160),
      title: clip(redact(input.title, secrets), 160),
      active: true,
    },
    character: {
      cardId: clip(redact(input.cardId, secrets), 160),
      name: clip(redact(input.characterName, secrets), 160),
    },
    chat: {
      providerId: clip(redact(input.providerId, secrets), 160),
      modelId: clip(redact(input.modelId, secrets), 160),
      phase: input.phase,
    },
    voice: {
      speech: {
        providerId: clip(redact(input.speech.providerId, secrets), 160),
        modelId: clip(redact(input.speech.modelId, secrets), 160),
        voiceId: clip(redact(input.speech.voiceId, secrets), 160),
        ready: input.speech.ready,
      },
      hearing: {
        providerId: clip(redact(input.hearing.providerId, secrets), 160),
        modelId: clip(redact(input.hearing.modelId, secrets), 160),
        ready: input.hearing.ready,
      },
    },
    tasks: projectAgentTasks(input.sessionId, input.messages, secrets).slice(0, 100),
    ...(input.memory ? { memory: input.memory } : {}),
  }
}

function turnRecord(
  message: Extract<ChatHistoryItem, { role: 'assistant' }>,
  sessionId: string,
  reply: string,
  openTools: number,
  toolPhases: AgentTaskPhase[],
  secrets: string[],
): AgentTaskRecord {
  const id = `${message.id ?? 'assistant'}-turn`
  const startedAt = message.createdAt
  if (message.interrupted) {
    return { id, sessionId, kind: 'turn', phase: 'cancelled', errorCode: 'cancelled', startedAt, summary: 'The turn was cancelled.' }
  }
  if (openTools > 0) {
    return { id, sessionId, kind: 'turn', phase: 'running', startedAt, summary: 'The turn is running.' }
  }
  if (reply) {
    return { id, sessionId, kind: 'turn', phase: 'succeeded', startedAt, summary: clip(redact(reply, secrets)) }
  }
  if (toolPhases.includes('timed-out')) {
    return { id, sessionId, kind: 'turn', phase: 'timed-out', errorCode: 'timeout', startedAt, summary: 'A tool call timed out.' }
  }
  if (toolPhases.includes('failed')) {
    return { id, sessionId, kind: 'turn', phase: 'failed', errorCode: 'failed', startedAt, summary: 'A tool call failed.' }
  }
  if (toolPhases.includes('rejected')) {
    return { id, sessionId, kind: 'turn', phase: 'rejected', errorCode: 'rejected', startedAt, summary: 'A tool call was rejected.' }
  }
  if (toolPhases.includes('cancelled')) {
    return { id, sessionId, kind: 'turn', phase: 'cancelled', errorCode: 'cancelled', startedAt, summary: 'The turn was cancelled.' }
  }
  if (toolPhases.includes('succeeded')) {
    return { id, sessionId, kind: 'turn', phase: 'succeeded', startedAt, summary: 'The turn finished.' }
  }
  return { id, sessionId, kind: 'turn', phase: 'started', startedAt, summary: 'The turn started.' }
}

function toolPhase(
  result: ChatSlicesToolCallResult | undefined,
  interrupted: boolean,
  secrets: string[],
): { phase: AgentTaskPhase, errorCode?: ToolResultErrorCode, summary: string } {
  if (!result) {
    if (interrupted) {
      return { phase: 'cancelled', errorCode: 'cancelled', summary: 'The tool call was cancelled.' }
    }
    return { phase: 'running', summary: 'The tool call is still running.' }
  }

  const annotated = annotateToolCallResult(result)
  return {
    phase: phaseFrom(annotated.status),
    errorCode: annotated.errorCode,
    summary: clip(redact(textFrom(annotated.result), secrets)) || 'The tool call finished.',
  }
}

function phaseFrom(status: ToolResultStatus | undefined): AgentTaskPhase {
  if (status === 'pending-confirmation')
    return 'pending-confirmation'
  if (status === 'succeeded')
    return 'succeeded'
  if (status === 'timed-out')
    return 'timed-out'
  if (status === 'rejected')
    return 'rejected'
  if (status === 'cancelled')
    return 'cancelled'
  if (status === 'failed')
    return 'failed'
  return 'succeeded'
}

function present(task: AgentTaskRecord): AgentTaskRecord {
  return {
    id: task.id,
    sessionId: task.sessionId,
    kind: task.kind,
    phase: task.phase,
    summary: task.summary,
    ...(task.toolName ? { toolName: task.toolName } : {}),
    ...(task.startedAt === undefined ? {} : { startedAt: task.startedAt }),
    ...(task.endedAt === undefined ? {} : { endedAt: task.endedAt }),
    ...(task.durationMs === undefined ? {} : { durationMs: task.durationMs }),
    ...(task.errorCode ? { errorCode: task.errorCode } : {}),
  }
}

function textFrom(content: unknown): string {
  if (typeof content === 'string')
    return content
  if (!Array.isArray(content))
    return ''
  return content.map((part) => {
    if (typeof part === 'object' && part && 'text' in part && typeof part.text === 'string')
      return part.text
    return ''
  }).join('')
}

function redact(text: string, secrets: string[]): string {
  return sanitizeConnectionError(text, secrets)
}

function clip(text: string, limit = summaryLimit): string {
  const trimmed = text.trim()
  if (trimmed.length <= limit)
    return trimmed
  return `${trimmed.slice(0, limit)}…`
}
